import { useEffect } from 'react';

/**
 * Custom hook to ensure focused elements are visible in the viewport
 * Behavior:
 * - For elements that fit: Shows the entire element (prioritizes top if both ends cut off)
 * - For large elements: Ensures top is properly positioned
 *
 * @param headerOffset - Space to maintain from top of viewport (default: 100px)
 * @param footerOffset - Space to maintain from bottom of viewport (default: 100px)
 */
export function useFocusScroll(headerOffset: number = 100, footerOffset: number = 100) {
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const focusedElement = e.target as HTMLElement;
      if (!focusedElement) return;

      const rect = focusedElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const elementHeight = rect.height;
      const availableSpace = viewportHeight - headerOffset - footerOffset;

      // Check if element fits in available viewport space
      const elementFitsInViewport = elementHeight <= availableSpace;

      if (elementFitsInViewport) {
        // For elements that fit: ensure the entire element is visible
        const isTopCutOff = rect.top < headerOffset;
        const isBottomCutOff = rect.bottom > viewportHeight - footerOffset;

        if (isTopCutOff || isBottomCutOff) {
          let scrollNeeded = 0;

          if (isTopCutOff && isBottomCutOff) {
            scrollNeeded = rect.top - headerOffset;
          } else if (isTopCutOff) {
            scrollNeeded = rect.top - headerOffset;

            const bottomAfterScroll = rect.bottom - scrollNeeded;
            if (bottomAfterScroll > viewportHeight - footerOffset) {
              scrollNeeded = rect.bottom - (viewportHeight - footerOffset);
            }
          } else {
            scrollNeeded = rect.bottom - (viewportHeight - footerOffset);
          }

          window.scrollBy({
            top: scrollNeeded,
            behavior: 'smooth',
          });
        }
      } else {
        // For elements larger than viewport: just ensure top is visible
        if (rect.top < headerOffset || rect.top > headerOffset + 50) {
          const scrollNeeded = rect.top - headerOffset;
          window.scrollBy({
            top: scrollNeeded,
            behavior: 'smooth',
          });
        }
      }
    };

    document.addEventListener('focusin', handleFocus);

    return () => {
      document.removeEventListener('focusin', handleFocus);
    };
  }, [headerOffset, footerOffset]);
}
